import {
  getGetPlatformCatalogsQueryKey,
  PaginatedPlatformCatalogListItemsDTO,
  useDeletePlatformCatalogById,
  useGetPlatformCatalogs,
} from "@bvhi/backend-api";
import { Card, CardContent, GridPaginationModel, GridSortModel } from "@bvhi/ui-base";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { BVHIDataGrid } from "../../../../../../../components/datagrid/bvhi-datagrid";
import { useAuthContext } from "../../../../../../../context/use-auth-context";
import { SecuredDeleteDialog } from "../../../../../../../dialogs/secured-delete-dialog";
import { useToast } from "../../../../../../../hooks/use-toast";
import { Route } from "../../../../../../../routes/_main/app/catalogs.index";
import { toNarrowedTypeOrNull } from "../../../../../../../utils/ts";
import { useCatalogsDataGridDefinitions } from "../../hooks/use-catalogs-datagrid-definitions";
import { useCatalogsSortParams } from "../../hooks/use-catalogs-sort-params";

interface Props {
  updateCallback: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
}

export const CatalogsDataGrid = forwardRef<HTMLDivElement, Props>(({ updateCallback }, ref) => {
  const searchParams = Route.useSearch();
  const { t } = useTranslation(["catalogs", "dialogs"]);
  const { currentUser } = useAuthContext();
  const queryClient = useQueryClient();
  const { mutate: deletePlatformCatalogById } = useDeletePlatformCatalogById();
  const { getSortModelForBackendString } = useCatalogsSortParams();

  const { data, isFetching } = useGetPlatformCatalogs(currentUser?.organisation?.id ?? "", searchParams, {
    query: { throwOnError: (error) => error.status === 500 || error.status === 403, placeholderData: keepPreviousData },
  });

  const [catalogIdToDelete, setCatalogIdToDelete] = useState<string | null>(null);
  const { pushDeleteSuccessToast, pushDeleteErrorToast } = useToast();

  const handleDelete = useCallback(
    (catalogId: string) => {
      setCatalogIdToDelete(catalogId);
    },
    [setCatalogIdToDelete]
  );

  const { columns } = useCatalogsDataGridDefinitions(handleDelete);

  const handleCornfirmDialogClose = useCallback(
    (confirmed: boolean) => {
      if (!confirmed || !catalogIdToDelete || !currentUser?.organisation?.id) {
        setCatalogIdToDelete(null);
        return;
      }
      deletePlatformCatalogById(
        { supplierId: currentUser.organisation.id, platformCatalogId: catalogIdToDelete },
        {
          onSuccess: () => {
            pushDeleteSuccessToast(t("catalogs:catalogs.catalog"));
          },
          onError: () => {
            pushDeleteErrorToast(t("catalogs:catalogs.catalog"));
          },
          onSettled: () => {
            setCatalogIdToDelete(null);
            void queryClient.invalidateQueries({ queryKey: getGetPlatformCatalogsQueryKey(currentUser.organisation?.id ?? "") });
          },
        }
      );
    },
    [deletePlatformCatalogById, pushDeleteErrorToast, pushDeleteSuccessToast, queryClient, t, catalogIdToDelete, currentUser]
  );

  const catalogs = toNarrowedTypeOrNull<PaginatedPlatformCatalogListItemsDTO>(data?.data, "numberOfItems");
  return (
    <div ref={ref}>
      <Card sx={{ minHeight: 300 }}>
        <CardContent restrictContentWidth={false}>
          <BVHIDataGrid
            columns={columns}
            initialSortModel={getSortModelForBackendString(searchParams.sort && searchParams.sort.length >= 1 ? searchParams.sort[0] : "")}
            loading={isFetching}
            rowCount={catalogs?.numberOfItems ?? -1}
            rows={catalogs?.payload ?? []}
            totalPages={catalogs?.numberOfPages ?? 0}
            updateCallback={updateCallback}
            initalPaginationModel={{
              page: searchParams.page ? searchParams.page : 0,
              pageSize: searchParams.size ? searchParams.size : 25,
            }}
          />
        </CardContent>
      </Card>
      <SecuredDeleteDialog
        onClose={handleCornfirmDialogClose}
        open={catalogIdToDelete !== null}
        overwrite={{ title: t("dialogs:dialogs.delete.catalog.title") }}
      />
    </div>
  );
});
CatalogsDataGrid.displayName = "CatalogsDataGrid";
